/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useCallback, useEffect, useState } from "react";
import { Box, Modal, Typography } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Close as CloseIcon } from "@mui/icons-material";
import Input from "components/Input";
import { useAppDispatch } from "store/hooks";
import { useAuth } from "context/AuthContext";
import * as Styled from "./styles";
import { cpfOrCnpjFormat, EMAIL_PATTERN_REGEX } from "utils/formatByText";
import DealerCare from "services/dealerCareApi";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import i18next from "i18next";
import { toast } from "react-toastify";
import Button from "components/atoms/Button";
import { cpf } from "cpf-cnpj-validator";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { validateSurname } from "pages/ManageUsers/helpers";

interface ModalAddUserProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseSuccess: () => void;
}

interface AddNewUserData {
  email: string;
  name: string;
  surname: string;
  cpf?: string;
  imageUrl?: string;
  profile: string;
  cellphone: string;
}

const optionsProfile = [
  { value: "administrator" },
  { value: "deliveryman" },
  { value: "attendant" },
];

export const cellphone = (cell: string) => {
  const areaCode = cell.replace(/\D/g, "").substring(2, -2);
  const number = cell.replace(/\D/g, "").substring(2, cell.length - 2);

  return {
    countryCode: "55",
    areaCode,
    number,
  };
};

const ModalAddUser = ({
  isOpen,
  onCloseSuccess,
  onClose,
}: ModalAddUserProps) => {
  const { dealerId } = useAuth();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
    setError,
    setValue,
    reset,
  } = useForm<AddNewUserData>();
  const [isCreateSucess, setCreateSucess] = useState(false);
  const [password, setPassword] = useState("");

  const profileSelected = watch("profile");
  const isSelectedDeliveryman = profileSelected === "deliveryman";

  const handleDealerUserCreate: SubmitHandler<AddNewUserData> = useCallback(
    async (data) => {
      const cpfValue = watch("cpf")?.replace(/[^\d]/g, "");
      if (
        (!cpf?.isValid(String(cpfValue)) && cpfValue?.length === 11) ||
        (String(cpfValue)?.length < 11 && data?.profile === "deliveryman")
      ) {
        setError("cpf", { type: "manual", message: "CPF inválido" });
        return;
      }
      try {
        dispatch(setIsSpinnerVisible(true));

        const { data: dataResponse } = await DealerCare.post<{
          data: {
            cpf: string;
            email: string;
            id: string;
            password: string;
          };
        }>(`/v1/dealers/${dealerId}/users`, {
          cpf:
            data?.cpf?.length && data?.profile === "deliveryman"
              ? data?.cpf.replace(/\.|-/gm, "")
              : undefined,
          email:
            data?.email?.length && data?.profile !== "deliveryman"
              ? data?.email
              : undefined,
          name: data.name,
          surname: data.surname,
          profile: data.profile,
        });
        dispatch(setIsSpinnerVisible(false));
        if (data?.profile === "deliveryman" && dataResponse.data.password) {
          setPassword(dataResponse.data.password);
        }
        setCreateSucess(true);
      } catch (error: any) {
        dispatch(setIsSpinnerVisible(false));

        if (
          error?.response?.data?.errors[0]?.code === "User.EmailAlreadyUsed"
        ) {
          toast("E-mail já cadastrado, por favor insira um novo", {
            type: "error",
            autoClose: false,
          });
        } else if (
          error?.response?.data?.errors[0]?.code === "User.CpfAlreadyUsed"
        ) {
          toast("CPF já cadastrado, por favor insira um novo", {
            type: "error",
            autoClose: false,
          });
        } else {
          toast(i18next.t("failedCreateUserPleaseAgain").toString(), {
            type: "error",
            autoClose: false,
          });
        }
      }
    },
    [watch, setError, dispatch, dealerId],
  );

  const onCloseModal = () => {
    onCloseSuccess();
    onClose();
    reset();
    setCreateSucess(false);
  };

  useEffect(() => {
    setValue("cpf", "");
    setValue("surname", "");
    setValue("email", "");
  }, [watch, setValue]);

  useEffect(() => {
    setValue("cpf", "");
    setValue("email", "");
  }, [profileSelected, setValue]);

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        setPassword("");
        reset();
        return onClose();
      }}
      closeAfterTransition
      keepMounted
    >
      <Styled.Content>
        {!isCreateSucess ? (
          <>
            <Typography variant="h6" mb={2}>
              {t("addUser")}
            </Typography>

            <Typography variant="subtitle1" mb={2}>
              {t("registerEmailLoginNewUser")}
            </Typography>
            <Styled.Form onSubmit={handleSubmit(handleDealerUserCreate)}>
              <Styled.Row>
                <Input
                  name="name"
                  error={errors.name}
                  label={`${t("name")}*`}
                  register={register}
                  fullWidth
                  inputProps={{
                    maxLength: 256,
                  }}
                  registerOptions={{
                    pattern: {
                      value: /^[A-Za-zÀ-ÖØ-öø-ÿ]{2,} ?$/,
                      message: "Nome inválido",
                    },
                    required: t("requiredField"),
                    onBlur: (e) => setValue("name", e.target.value?.trim()),
                  }}
                  sx={{ mb: 1, height: 56 }}
                />
                <Input
                  name="surname"
                  error={errors.surname}
                  label={`${t("surname")}*`}
                  register={register}
                  fullWidth
                  inputProps={{
                    maxLength: 256,
                  }}
                  registerOptions={{
                    required: t("requiredField"),
                    validate: (value: string) => validateSurname(value),
                  }}
                  sx={{ mb: 2 }}
                />
              </Styled.Row>
              <Styled.Row>
                <Input
                  select
                  name="profile"
                  label={`${t("permission")}*`}
                  error={errors.profile}
                  register={register}
                  selectOptions={optionsProfile.map((option) => ({
                    value: option.value,
                    name: t(option.value),
                  }))}
                  sx={{ ml: 1, height: 56 }}
                  fullWidth
                  registerOptions={{
                    required: t("requiredField"),
                  }}
                />

                {isSelectedDeliveryman ? (
                  <Input
                    name="cpf"
                    error={errors.cpf}
                    label="CPF*"
                    value={cpfOrCnpjFormat(watch("cpf") || "")}
                    register={register}
                    fullWidth
                    type="text"
                    inputProps={{
                      maxLength: 14,
                    }}
                    registerOptions={{
                      required: t("requiredField"),
                      onBlur: (e) => setValue("cpf", e.target.value?.trim()),
                      onChange: (e) => {
                        return setValue(
                          "cpf",
                          cpfOrCnpjFormat(e.target.value.replace(/[^\d]/g, "")),
                        );
                      },
                    }}
                    sx={{ mb: 1 }}
                  />
                ) : (
                  <Input
                    name="email"
                    error={errors.email}
                    label={`${t("email")}*`}
                    register={register}
                    fullWidth
                    registerOptions={{
                      pattern: {
                        value: EMAIL_PATTERN_REGEX,
                        message: "Endereço de e-mail inválido",
                      },
                      required: t("requiredField"),
                      onChange: (e) => setValue("email", e.target.value),
                      onBlur: (e) => setValue("email", e.target.value?.trim()),
                    }}
                    sx={{ mb: 1 }}
                  />
                )}
              </Styled.Row>
              {isSelectedDeliveryman && (
                <Styled.Instructions>
                  <ErrorOutlineIcon />
                  <Box display="flex" alignItems="center" ml={1}>
                    <Typography variant="body2" ml={0.5} color="#444142">
                      <Typography
                        variant="body1"
                        sx={{ display: "inline" }}
                        fontWeight="medium"
                        mr={0.5}
                      >
                        {`${t("important")}:`}
                      </Typography>
                      {t("instructionsUserText")}
                    </Typography>
                  </Box>
                </Styled.Instructions>
              )}

              <Styled.ActionsContainer>
                <Button
                  color="inherit"
                  variant="outlined"
                  onClick={() => {
                    reset();
                    return onClose();
                  }}
                >
                  {t("close").toUpperCase()}
                </Button>
                <Button color="error" variant="contained" type="submit">
                  {t("add").toUpperCase()}
                </Button>
              </Styled.ActionsContainer>
            </Styled.Form>
          </>
        ) : (
          <>
            <Typography variant="h6" mb={2}>
              {profileSelected === "deliveryman"
                ? t("driverAddedSuccessfully")
                : t("userAddedSuccessfully")}
            </Typography>

            <Typography variant="subtitle1">
              {profileSelected === "deliveryman"
                ? t("forFirstAccess")
                : t("passwordCreatedSuccessfully")}
            </Typography>
            <Typography variant="subtitle1" mb={2}>
              {profileSelected !== "deliveryman" &&
                t("sendEmailTemporaryPassword")}
            </Typography>
            <Styled.ViewData>
              <Typography variant="subtitle1" mb={2}>
                {profileSelected === "deliveryman"
                  ? t("temporaryPassword")
                  : t("emailSentTo")}
                <Typography variant="h5">
                  {profileSelected === "deliveryman"
                    ? password
                    : getValues().email}
                </Typography>
              </Typography>
            </Styled.ViewData>
            <Styled.Form>
              <Styled.ActionsContainer>
                <Button
                  color="inherit"
                  endIcon={<CloseIcon />}
                  variant="outlined"
                  onClick={() => {
                    reset();
                    setPassword("");
                    return onCloseModal();
                  }}
                >
                  {t("close").toUpperCase()}
                </Button>
              </Styled.ActionsContainer>
            </Styled.Form>
          </>
        )}
      </Styled.Content>
    </Modal>
  );
};

export default ModalAddUser;
